import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import localforage from 'localforage'
import { queryClient } from './trpc'

/**
 * Helper to check authentication status and redirect to login if needed
 */
export function useAuthCheck() {
  const navigate = useNavigate()

  // Function to check auth status and redirect if needed
  // const checkAuth = async () => {
  //   // First try the electron API if available
  //   const { token, expiry } = await getAuthToken()

  //   if (!token || !expiry) {
  //     clearAuthData()
  //     toast.error('You need to login to access this page')
  //     navigate({ to: '/login?error=session-not-found&redirect_uri=' + window.location.pathname })
  //     return false
  //   }

  //   if (Date.now() >= expiry) {
  //     toast.error('Your session has expired. Please login again.')
  //     navigate({ to: '/login?error=session-expired&redirect_uri=' + window.location.pathname })
  //     return false
  //   }

  //   return true
  // }

  // Set up an event listener for auth expired events from the main process
  const setupAuthListener = () => {
    // Only setup if we're in Electron
    if (!window._main_api?.onAuthSessionExpired) return () => { }

    return window._main_api.onAuthSessionExpired(() => {
      toast.error('Your session has expired. Please login again.')
      navigate({ to: '/login?error=session-expired&redirect_uri=' + encodeURIComponent(window.location.href) })
    })
  }

  return { setupAuthListener }
}

/**
 * Save authentication data consistently between web and electron environments
 */
export function saveAuthData(token: string, expiryMs: number) {
  // Store in localStorage for web app use
  localStorage.setItem('auth:token', token)
  localStorage.setItem('auth:expiry', expiryMs.toString())

  // If in Electron, also send to main process
  if (window._main_api?.sendAuthTokenToMain) {
    window._main_api.sendAuthTokenToMain(token, expiryMs)
  }

  return { token, expiryMs }
}

/**
 * Cancel all pending queries - call this BEFORE sign-out to prevent 401 errors
 */
export function cancelAllQueries() {
  try {
    queryClient.cancelQueries()
    console.log('>>> All pending queries cancelled')
  } catch (error) {
    console.error('Error cancelling queries:', error)
  }
}

/**
 * Clear authentication data consistently between web and electron environments
 * This clears ALL cache layers: localStorage, IndexedDB (localforage), and React Query cache
 */
export async function clearAuthData() {
  console.log('>>> Clearing all authentication and cache data')

  // 1. Cancel any pending queries first to prevent 401 errors during logout
  cancelAllQueries()

  // 2. Clear from localStorage
  localStorage.removeItem('auth:session')
  localStorage.removeItem('auth:token')
  localStorage.removeItem('auth:expiry')
  localStorage.removeItem('auth:activeOrg')
  localStorage.removeItem('auth:activeWorkspace')
  localStorage.removeItem('auth:activeWorkspaceId')
  localStorage.removeItem('partner:delegation')

  // 3. Clear IndexedDB (localforage) - document cache and other persistent data
  try {
    await localforage.clear()
    console.log('>>> LocalForage cache cleared')
  } catch (error) {
    console.error('Error clearing localforage:', error)
  }

  // 4. Clear React Query cache - all API queries
  try {
    queryClient.clear()
    console.log('>>> React Query cache cleared')
  } catch (error) {
    console.error('Error clearing query cache:', error)
  }

  // 5. If in Electron, also clear from main process
  if (window._main_api?.signOut) {
    console.log('>>> Clearing auth data in main process')
    window._main_api.signOut()
  }
}

export async function getAuthToken() {
  let token = null
  let expiry = null
  
  if (window._main_api) {
    try {
      const result = await window._main_api.getAuthToken()
      // console.log('>>> main API getAuthToken', result)
      token = result?.token
      expiry = result?.expiry
    } catch (error) {
      console.error('Error validating auth via Electron:', error)
      // Fall through to local storage check
    }
  }

  if (!token || !expiry) {
    // Fallback to localStorage check
    token = localStorage.getItem('auth:token')
    expiry = localStorage.getItem('auth:expiry')
  }

  if (expiry) {
    try {
      const expiryTime = parseInt(expiry, 10)
      return { token, expiry: expiryTime }
    } catch (error) {
      console.error('Error parsing expiry:', error)
      return { token, expiry: null }
    }
  } else {
    return { token, expiry: null }
  }
}